import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/store';
import { selectUserRole } from '@/store/slices/authSlice';
import { normalizeRole, type EffectiveRole } from '@/shared/utils/roles';

interface ProtectedRouteProps {
  children: React.ReactNode;
  isAuthenticated: boolean;
  requiredRoles?: EffectiveRole[];
}

/**
 * ProtectedRoute Component
 * Wraps routes that require authentication
 * Redirects to login if not authenticated
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  isAuthenticated,
  requiredRoles,
}) => {
  const location = useLocation();
  const role = useAppSelector(selectUserRole);
  const effectiveRole = normalizeRole(role);

  if (!isAuthenticated) {
    // Redirect to the main app login (handled by parent app in Module Federation)
    // Store the attempted URL for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRoles && !requiredRoles.includes(effectiveRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
